import { Autocomplete, Box, Dialog, TextField } from '@mui/material';
import { formatDuration } from '../../../helpers/formatDuration';
import { useCallback, useState } from 'react';
import { LayoutRegister } from '../../../layouts';
import { IBusinessEmployeeServiceView, IServiceView, feedback } from '../../../services';
import { Button } from '../../MUI/button/Button';
import { List } from '../list/List';
import { DurationAutocomplete } from '../duration-autocomplete/DurationAutocomplete';

interface IProps {
  businessEmployeeId?: number;
  businessId: number;
  services?: IServiceView[];
  employeeServices: {
    [key: number]: IBusinessEmployeeServiceView[];
  };
  setEmployeeServices: React.Dispatch<React.SetStateAction<{
    [key: number]: IBusinessEmployeeServiceView[];
  }>>;
}

export const BusinessEmployeeService: React.FC<IProps> = ({ businessEmployeeId, businessId, services, employeeServices, setEmployeeServices }) => {
  const [isEditService, setIsEditService] = useState(false);
  const [openDialogService, setOpenDialogService] = useState(false);
  const [serviceDuration, setServiceDuration] = useState<number | null>(null);
  const [selectedServiceEmployee, setSelectedServiceEmployee] = useState<IServiceView>();

  const onClickSaveService = useCallback(() => {
    if (selectedServiceEmployee?.id && serviceDuration) {
      const employeeService: IBusinessEmployeeServiceView = {
        id: 0,
        businessEmployeeId: businessEmployeeId || 0,
        serviceId: selectedServiceEmployee.id,
        serviceDuration: serviceDuration,
        service: selectedServiceEmployee
      };

      if (isEditService) {
        setEmployeeServices((previous) => ({
          ...previous,
          [businessId]: previous[businessId].map((item) =>
            item.serviceId === employeeService.serviceId ? employeeService : item
          ),
        }));
      } else {
        const currentService = (employeeServices[businessId] || []).some(item => item.serviceId == selectedServiceEmployee.id);

        if (currentService) {
          feedback('Serviço já selecionado para o profissional.', 'error');
          return;
        }

        setEmployeeServices(previous => ({
          ...previous,
          [businessId]: [...(previous[businessId] || []), employeeService]
        }));
      }

      handleClose();
    } else {
      feedback('Selecione um serviço para salvar.', 'error');
    }
  }, [selectedServiceEmployee, serviceDuration, employeeServices]);

  const onClickDeleteService = useCallback((id: number) => {
    setEmployeeServices(previous => ({
      ...previous,
      [businessId]: (previous[businessId] || []).filter(item => item.serviceId !== id)
    }));
  }, []);

  const onClickEditService = useCallback((id: number) => {
    const service = services?.find(item => item.id === id);
    const employeeService = (employeeServices[businessId] || []).find(item => item.serviceId === id);

    setIsEditService(true);
    setSelectedServiceEmployee(service);
    setServiceDuration(employeeService?.serviceDuration ?? null);
    setOpenDialogService(true);
  }, [employeeServices]);

  const handleClose = useCallback(() => {
    setIsEditService(false);
    setSelectedServiceEmployee(undefined);
    setServiceDuration(null);
    setOpenDialogService(false);
  }, []);

  return (
    <Box>
      <Box display='flex' justifyContent='center'>
        <Button
          label='+ SERVICO'
          borderRounded={false}
          onClick={() => setOpenDialogService(true)}
        />
      </Box>

      <List
        list={(employeeServices[businessId] || []).map((item) => ({ id: item.serviceId, description: item.service?.description + ' - ' + formatDuration(item.serviceDuration) }))}
        onClickEdit={onClickEditService}
        onClickDelete={onClickDeleteService}
      />

      <Dialog
        open={openDialogService}
        onClose={handleClose}
        transitionDuration={0}
      >
        <LayoutRegister header='Novo serviço para o profissional' heightDefault={200} onClickSave={onClickSaveService}>
          <Box display='flex' flexDirection='column' gap={2}>
            <Autocomplete
              options={services || []}
              getOptionLabel={(opt) => opt.description}
              value={selectedServiceEmployee}
              disabled={isEditService}
              onChange={(_, service) => {
                if (service) {
                  setSelectedServiceEmployee(service);
                  setServiceDuration(service.serviceDuration);
                }
              }}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Serviço'
                  variant='outlined'
                />
              )}
            />

            <DurationAutocomplete
              label='Duração do serviço (min)'
              value={serviceDuration}
              disabled={!services}
              defaultDuration={selectedServiceEmployee?.serviceDuration}
              onChange={(value) => setServiceDuration(value || undefined)}
            />
          </Box>
        </LayoutRegister>
      </Dialog>
    </Box>
  );
};